import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Receipt, Loader2, CreditCard, CheckCircle2 } from 'lucide-react';
import { PaymentError } from '../components/PaymentError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Public page reached from the invoice email: shows the invoice and starts Stripe Checkout.
export default function InvoicePay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get('t') || '';
  const cancelled = params.get('cancelled') === '1';
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invoices/${id}/public`, { params: { t } }).then((r) => setInv(r.data)).catch(() => setErr('This invoice link is not valid. Please check the link in your email or contact the Treasurer.'));
  }, [id, t]);

  const pay = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/invoices/${id}/pay`, { t, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not start the payment.'); setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 fade-in" data-testid="invoice-pay-page">
      <div className="flex items-center gap-3 mb-2"><Receipt size={28} style={{ color: 'var(--maroon)' }} /><h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Invoice {inv?.number || ''}</h1></div>
      <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
      <PaymentError err={err} testid="invoice-pay-error" className="text-sm mb-4 rounded p-3" />
      {cancelled && !err && <p className="text-sm mb-4" style={{ color: 'var(--olive)' }} data-testid="invoice-pay-cancelled">Payment was cancelled — you can try again below.</p>}
      {!inv && !err && <p className="text-sm flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading invoice…</p>}
      {inv && (
        <div className="bg-white rounded-lg border p-5 shadow-sm" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>To: <strong style={{ color: 'var(--ink)' }}>{inv.name}</strong> ({inv.email}){inv.due_date ? ` · Due ${inv.due_date}` : ''}</p>
          <table className="w-full text-sm mb-3" data-testid="invoice-pay-lines">
            <tbody>
              {inv.lines.map((l, i) => <tr key={i} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}><td className="py-1.5">{l.description}</td><td className="py-1.5 text-right whitespace-nowrap">{l.qty} × {money(l.unit_price)}</td></tr>)}
              <tr className="border-t" style={{ borderColor: 'rgba(0,0,0,0.2)' }}><td className="py-2 font-title">Total (AUD)</td><td className="py-2 text-right font-title" data-testid="invoice-pay-total">{money(inv.total)}</td></tr>
            </tbody>
          </table>
          {inv.notes && inv.status !== 'lapsed' && <p className="text-sm mb-3 whitespace-pre-line" style={{ color: 'var(--olive)' }}>{inv.notes}</p>}
          {inv.status === 'paid' ? (
            <p className="inline-flex items-center gap-2 text-sm font-title" style={{ color: '#1c6b2c' }} data-testid="invoice-pay-paid"><CheckCircle2 size={18} /> Paid{inv.paid_at ? ` on ${new Date(inv.paid_at).toLocaleDateString()}` : ''} — thank you.</p>
          ) : inv.status === 'void' ? (
            <p className="text-sm" style={{ color: 'var(--olive)' }}>This invoice has been cancelled.</p>
          ) : inv.status === 'lapsed' ? (
            <PaymentError testid="invoice-pay-lapsed-state" err={{ code: 'membership_lapsed', apply_url: inv.lapsed?.apply_url, on_hold: inv.lapsed?.on_hold,
              message: `This renewal cannot be paid yet: the membership year ended on ${inv.lapsed?.expired || '—'} and the ${inv.lapsed?.grace_days || ''}-day grace period closed on ${inv.lapsed?.grace_until || '—'}, so the membership is outside the grace period. ${inv.lapsed?.on_hold ? 'Your renewal is on hold pending executive approval — we will email you as soon as you can pay.' : 'The committee has been told.'}${inv.lapsed?.apply_url ? ' Alternatively you can re-apply — your details are already filled in on the application.' : ' Or contact the Secretary about re-applying.'}` }} />
          ) : (
            <button onClick={pay} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded font-title disabled:opacity-60" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="invoice-pay-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay {money(inv.total)} securely
            </button>
          )}
          <p className="text-xs mt-3" style={{ color: 'var(--olive)' }}>Payments are processed by Stripe. A receipt is emailed to you automatically.</p>
        </div>
      )}
    </div>
  );
}
